import React from "react";
import { useEffect, useState } from "react";
import { useMediaQuery } from 'react-responsive';
import { ReactComponent as CameraSvg } from '../../assets/svg/camera.svg';
import { ReactComponent as LinkedIn } from '../../assets/svg/linkedIn-2.svg';
import { ReactComponent as Facebook } from '../../assets/svg/facebook.svg';
import { ReactComponent as Twitter } from '../../assets/svg/twitter.svg';
import { ReactComponent as Youtube } from '../../assets/svg/youtube.svg';
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';
import { Switch } from "../../components/Shared/Buttons/Switch";
import { Button } from "../Shared/Buttons/Button";
import { validateImageFile, validateEmail, validateTelephoneNumber } from "../../helpers/Common";
import { TextInput } from "../Shared/Inputs/TextInput";
import { TextAreaInput } from "../Shared/Inputs/TextAreaInput";
import { useAlert } from "../../hooks/useAlert";
import { Organization as OrganizationModel, OrganizationValidation } from "../../models/Organization"
import { uploadOrganizationLogo, getOrganization, updateOrganization } from "../../services/RecruiterService";
import useErrorPage from "../../hooks/useErrorPage";
import { ResponseModel } from "../../models/ResponseModel";
import { LoggedRecruiter } from '../../models/LoggedRecruiter';
import { PopUpWindow } from '../Shared/PopUpWindow';
import { OrganizationLoader } from "../Loaders/OrganizationLoader";
import { CommonTexts as Texts } from '../../helpers/LayoutTexts';
import { UserType } from "../../enums/UserType";

interface Props {
    styles: any;
    recruiter: LoggedRecruiter;
}

const initialValidation: OrganizationValidation = {
    email: {},
    telephone: {},
    organizationName: {},
    address01: {},
    address02: {},
    address03: {}
}

export const Organization: React.FC<Props> = (props) => {
    const styles = props.styles;
    const recruiter = props.recruiter;
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [logoUploading, setLogoUploading] = useState<boolean>(false);
    const [toErrorPage] = useErrorPage();
    const [showAddressLines, setShowAddressLines] = useState<boolean>(false);
    const { Alert, openAlert } = useAlert();
    const [validation, setValidation] = useState<OrganizationValidation>(initialValidation);
    const [loading, setLoading] = useState<boolean>(true);
    const [uploadedLogo, setUploadedLogo] = useState<File>();
    const [logoURL, setLogoURL] = useState<string>("");
    const [organizationModel, setOrganizationModel] = useState<OrganizationModel>();
    
    useEffect(() => {
        getOrganizationModel();
    }, []);

    async function getOrganizationModel() {
        await getOrganization(recruiter.organizationId, recruiter.recruiterId, recruiter.token)
        .then((model: OrganizationModel) => {
            setOrganizationModel(model);
            setLogoURL(model.logoUri ?? Texts.defaultCompanyLogo);
            setLoading(false);
        })
        .catch((error) => {
            toErrorPage(error, UserType.Recruiter);
        });
    }

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setOrganizationModel((values: any) => ({ ...values, [name]: value }));
        if(validation[name] && validation[name]!.invalid) {
            validateInput(name, value);
        }
    }

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        validateInput(name, value);
    }

    const handleTextAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setOrganizationModel((values: any) => ({ ...values, [name]: value }));
    }

    const handlePlusIcon = () => {
        setShowAddressLines(!showAddressLines);
    };

    const validateInput = (name: string, value: string) => {
        if(name === "organizationName" && value === "") {
            setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Organization name is required."}}));    
        }
        else{
            setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
        }
        if(name === "email") {             
            if (value !== "" && !validateEmail(value)) {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Invalid email address!"}}));
            } 
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if(name === "telephone") {
            const trimmedValue = value.trim();
            if(trimmedValue !== "" && !validateTelephoneNumber(trimmedValue)) {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Invalid telephone number!"}}));
            } 
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if(name === "address01" || name === "address02" || name === "address03"){
            if(value !== "" && value.length < 5){
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Minimum allowable character length (5)"}}));
            }
            else if(value.length > 150){
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Exceeds maximum allowable character length (150)"}}));
            }
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
    }

    const validateForm = () => {
        let formValidation: OrganizationValidation = {
            organizationName: {valid: true},
            email: {valid: true},
            telephone: {valid: true},
            address01: {valid: true},
            address02: {valid: true},
            address03: {valid: true}
        }
        if(organizationModel?.organizationName === ""){
            formValidation.organizationName = {valid: false, invalid: true, invalidMessage: "Orgnization Name is required"};
        }
        if(organizationModel?.email !== "" && organizationModel?.email !== null && !validateEmail(organizationModel?.email!)) {
            formValidation.email = {valid: false, invalid: true, invalidMessage: "Invalid email address!"};
        }
        if(organizationModel?.telephone !== "" && !validateTelephoneNumber(organizationModel?.telephone!)) {
            formValidation.telephone = {valid: false, invalid: true, invalidMessage: "Invalid telephone number!"};
        }
        if(typeof organizationModel?.address01 === "string" && organizationModel?.address01 !== ""){
            if(organizationModel?.address01.length < 5){
                formValidation.address01 = {valid: false, invalid: true, invalidMessage: "Minimum allowable character length is 5."};
            }
            else if(organizationModel?.address01.length > 150){
                formValidation.address01 = {valid: false, invalid: true, invalidMessage: "Exceeds maximum allowable character length (150)."};
            }
        }
        if(typeof organizationModel?.address02 === "string" && organizationModel?.address02 !== ""){
            if(organizationModel?.address02.length < 5){
                formValidation.address02 = {valid: false, invalid: true, invalidMessage: "Minimum allowable character length is 5."};
            }
            else if(organizationModel?.address02.length > 150){
                formValidation.address02 = {valid: false, invalid: true, invalidMessage: "Exceeds maximum allowable character length (150)."};
            }
        }
        if(typeof organizationModel?.address03 === "string" && organizationModel?.address03 !== ""){
            if(organizationModel?.address03.length < 5){
                formValidation.address03 = {valid: false, invalid: true, invalidMessage: "Minimum allowable character length is 5."};
            }
            else if(organizationModel?.address03.length > 150){
                formValidation.address03 = {valid: false, invalid: true, invalidMessage: "Exceeds maximum allowable character length (150)."};
            }
        }
        setValidation(formValidation);
        const InvalidInputCount = Object.keys(formValidation).filter(key => formValidation[key]!.invalid).length;
        return InvalidInputCount;
    }

    const handleSwitchChange = (name: keyof OrganizationModel) => {
        setOrganizationModel(prev => ({...prev!, [name]: !prev![name]}));};

    const openLogoFileSelect = () => {
        document.getElementById("org-logo")?.click();
    }

    const handleLogoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let selectedFile = event.currentTarget?.files;
        if(selectedFile && selectedFile.length > 0) {
            let validation = validateImageFile(selectedFile[0]);
            if(validation.valid){
                let pictureURL = URL.createObjectURL(selectedFile[0]);
                setUploadedLogo(selectedFile[0]);
                setLogoURL(pictureURL);
            }
            else{
                openAlert("error", validation.invalidMessage!);
            }
            event.target.value = "";
        }
    }

    const removeSelectedLogo = () => {
        setUploadedLogo(undefined);
        setLogoURL(organizationModel?.logoUri ?? Texts.defaultCompanyLogo);
    }

    async function handleLogoUpload() {
        setLogoUploading(true);
        let formData = new FormData();
        formData.append("recruiterId", recruiter.recruiterId.toString()!); 
        formData.append("organizationId", recruiter.organizationId.toString()!);
        formData.append("organizationLogo", uploadedLogo!);
        await uploadOrganizationLogo(formData, recruiter.token)
        .then((response: ResponseModel) => {
            openAlert(response.status ? "success" : "error", response.message);
            if(response.status) {
               window.location.reload();
            }
        })
        .catch(() => {
            openAlert("error", "There was an error during the process!")
            setLogoURL(organizationModel?.logoUri ?? Texts.defaultCompanyLogo);
        })
        setLogoUploading(false);
        setUploadedLogo(undefined);
    }

    async function handleSaveChanges(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        let invalidInputCount = validateForm();
        if(invalidInputCount === 0) { 
            await updateOrganization(recruiter.recruiterId, organizationModel!, recruiter.token)
            .then((response: ResponseModel) => {
                if(response.status) {
                    openAlert("success", response.message);
                    setLoading(false);
                }
                else {
                    openAlert("error", response.message);
                }
            })
            .catch((error) => {
                toErrorPage(error, UserType.Recruiter);
            });
        }
        else {
            openAlert("error", "Some mandatory fields are invalid.");
            validateForm();
        }     
    }
    return(
        <>
            { loading ? <OrganizationLoader/> :<div style={styles.mainWrapper}>
                <div style={styles.leftWrapper}>
                    <div style={styles.profilePhoto}>
                        <span style={styles.logoName}>{"Logo"}</span>
                        <img style={styles.organizationLogo} src={logoURL}/>
                        <Button style={styles.logo} onClick={openLogoFileSelect}>
                            <CameraSvg style={styles.cameraIcon}/>
                        </Button>
                        <input type="file" name="org-logo" id="org-logo" onChange={handleLogoFileChange} style={styles.fileInput}/>
                    </div>
                    {/* This is loading only desktop platform */}
                    {!isMobile && organizationModel && <>
                    <div style={styles.tabs}>
                        <div style={styles.mainTab}>
                            <div>
                                <div style={styles.mainTabText}>{"All Recruiters"}</div>
                                <div style={styles.tabSubText}>{"Allow access"}</div>
                            </div>
                            <Switch styles={styles.tabSwitch} checked={organizationModel.allowAllRecruitersToViewEditJobs} handleClick={() => handleSwitchChange("allowAllRecruitersToViewEditJobs")}/>
                        </div>
                        <div style={styles.tab}>
                            <div style={styles.tabText}>{"Differently-Abled Friendly"}</div>
                            <Switch styles={styles.tabSwitch} checked={organizationModel.disabilityFriendly} handleClick={()=> handleSwitchChange("disabilityFriendly")}/>
                        </div>
                        <div style={styles.tab}>
                            <div style={styles.tabText}>{"Startup"} <br/>{"SL Registered"}</div>
                            <Switch styles={styles.tabSwitch} checked={organizationModel.isStartUpRegistered} handleClick={()=> handleSwitchChange("isStartUpRegistered")}/>
                        </div>
                        <div style={styles.tab}>
                            <div style={styles.tabText}>{"LGBT"} <br/>{"Friendly"}</div> 
                            <Switch styles={styles.tabSwitch} checked={organizationModel.lgbtFriendly} handleClick={()=> handleSwitchChange("lgbtFriendly")}/>
                        </div>   
                    </div></>}
                </div>
                <div style={styles.rightWrapper}>
                    <form style={styles.form} onSubmit={handleSaveChanges}>
                        <div style={styles.inputGroup}>
                            <div style={styles.inputWrapper}>
                                <label style={styles.label}>{"Organization Name"}</label>
                                <TextInput
                                    inputType={"text"}
                                    name={"organizationName"}
                                    value={organizationModel?.organizationName}
                                    styles={styles.textGroup}
                                    handleChange={handleInputChange}
                                    handleBlur={handleInputBlur}                           
                                    validation={validation.organizationName}
                                    disabled = {true}    
                                />
                            </div>
                            <div style={styles.inputWrapper}>
                                <label style={styles.label}>{"Tag Line"}</label>
                                <TextInput
                                    inputType={"text"}
                                    name={"tagLine"}
                                    value={organizationModel?.tagLine}
                                    styles={styles.textGroup}
                                    handleChange={handleInputChange}
                                    handleBlur={handleInputBlur}                              
                                /> 
                            </div>   
                        </div>
                        <div style={styles.textArea}>
                            <label style={styles.label}>{"Overview"}</label>
                            <TextAreaInput                
                                name={"description"}
                                value={organizationModel?.description}                                                  
                                handleChange={handleTextAreaChange}
                                styles={styles.textAreaInput}
                            />
                        </div>
                        <div style={styles.inputGroup}>
                            <div style={styles.inputWrapper}>
                                <label style={styles.label}>{"Number of Employees"}</label>
                                <TextInput
                                    inputType={"text"}
                                    name={"employees"}
                                    value={organizationModel?.employees}
                                    placeHolder={"# of Employees"}
                                    styles={styles.textGroup}
                                    handleChange={handleInputChange}
                                    handleBlur={handleInputBlur}                           
                                    validation={validation.Employees}    
                                />
                            </div>
                            <div style={styles.inputWrapper}>
                                <label style={styles.label}>{"City"}</label>
                                <TextInput
                                    inputType={"text"}
                                    name={"city"}
                                    value={organizationModel?.city}
                                    styles={styles.textGroup}
                                    handleChange={handleInputChange}
                                    handleBlur={handleInputBlur}                           
                                /> 
                            </div>   
                        </div>
                        <div style={styles.tinInputWrapper}>
                            <label style={styles.label}>{"TIN Number"}</label>
                            <TextInput
                                inputType={"text"}
                                name={"tinNumber"}
                                value={organizationModel?.tinNumber}
                                styles={styles.textInput}
                                handleChange={handleInputChange}
                                handleBlur={handleInputBlur}                               
                            /> 
                        </div>
                        <hr style={styles.line}/>
                        <div style={styles.inputWrapper}>
                            <label style={styles.label}>{"Website"}</label>
                            <TextInput
                                inputType={"text"}
                                name={"website"}
                                value={organizationModel?.website}
                                styles={styles.textInput}
                                handleChange={handleInputChange}
                                handleBlur={handleInputBlur}                               
                            /> 
                        </div>
                        <div style={styles.inputWrapper}>
                            <label style={styles.label}>{"Email"}</label>
                            <TextInput
                                inputType={"text"}
                                name={"email"}
                                value={organizationModel?.email}
                                styles={styles.textInput}
                                handleChange={handleInputChange}
                                handleBlur={handleInputBlur}                           
                                validation={validation.email}    
                            />
                        </div>  
                        <div style={styles.inputWrapper}>
                            <label style={styles.label}>{"Telephone"}</label>
                            <TextInput
                                inputType={"text"}
                                name={"telephone"}
                                value={organizationModel?.telephone}
                                styles={styles.textInput}
                                handleChange={handleInputChange}
                                handleBlur={handleInputBlur}                              
                                validation={validation.telephone}                              
                            /> 
                        </div>
                        <div style={styles.spanWrapper}>
                            <label style={styles.label}>{"Address"}</label>
                            <div style={styles.inputWithPlusIcon}>
                                <TextInput
                                    inputType={"text"}
                                    name={"address01"}
                                    value={organizationModel?.address01}
                                    styles={styles.textInput}
                                    handleChange={handleInputChange}
                                    handleBlur={handleInputBlur} 
                                    validation={validation.address01}                        
                                /> 
                                {!(showAddressLines || organizationModel?.address02 || organizationModel?.address03) && <div style={styles.iconWrapper} onClick={handlePlusIcon}>
                                    <PlusIcon style={styles.plusIcon}/>
                                </div>}
                            </div>
                            {(showAddressLines || organizationModel?.address02 || organizationModel?.address03) && 
                                <>
                                    <div style={styles.inputWrapper}>
                                        <label style={styles.label}>{"Address 02"}</label>
                                        <TextInput
                                            inputType={"text"}
                                            name={"address02"}
                                            value={organizationModel?.address02}
                                            styles={styles.textInput}
                                            handleChange={handleInputChange}
                                            handleBlur={handleInputBlur}
                                            validation={validation.address02}                               
                                        /> 
                                    </div>
                                    <div style={styles.inputWrapper}>
                                        <label style={styles.label}>{"Address 03"}</label>
                                        <TextInput
                                            inputType={"text"}
                                            name={"address03"}
                                            value={organizationModel?.address03}
                                            styles={styles.textInput}
                                            handleChange={handleInputChange}
                                            handleBlur={handleInputBlur}  
                                            validation={validation.address03}                              
                                        /> 
                                    </div>
                                </>
                            }   
                        </div>
                        <hr style={styles.line}/>
                        {/* This is loading only mobile platform */}
                        {isMobile && organizationModel && <> <div style={styles.tabs}>
                            <div style={styles.mainTab}>
                                <div>
                                    <div style={styles.mainTabText}>{"All Recruiters"}</div>
                                    <div style={styles.tabSubText}>{"Allow access"}</div>
                                </div>
                                <Switch styles={styles.tabSwitch} checked={organizationModel.allowAllRecruitersToViewEditJobs} handleClick={() => handleSwitchChange("allowAllRecruitersToViewEditJobs")}/>
                            </div>
                            <div style={styles.tab}>
                                <div style={styles.tabText}>{"Differently-Abled Friendly"}</div>
                                <Switch styles={styles.tabSwitch} checked={organizationModel.disabilityFriendly} handleClick={()=> handleSwitchChange("disabilityFriendly")}/>
                            </div>
                            <div style={styles.tab}>
                                <div style={styles.tabText}>{"Startup SL Registered"}</div>
                                <Switch styles={styles.tabSwitch} checked={organizationModel.isStartUpRegistered} handleClick={()=> handleSwitchChange("isStartUpRegistered")}/>
                            </div>
                            <div style={styles.tab}>
                                <div style={styles.tabText}>{"LGBT Friendly"}</div> 
                                <Switch styles={styles.tabSwitch} checked={organizationModel.lgbtFriendly} handleClick={()=> handleSwitchChange("lgbtFriendly")}/>
                            </div>   
                        </div>
                        <hr style={styles.line}/>
                        </>}
                        <div style={styles.inputWrapper}>
                            <label style={styles.socialInputLabel}>{"LinkedIn"}</label>
                            <div style={styles.inputWithIcon}>
                                <div style={styles.socialIconWrapper}>
                                    <LinkedIn style={{...styles.socialIcon, ...styles.socialIcon.linkedIn}}/>
                                </div>
                                <TextInput
                                    inputType={"text"}
                                    name={"linkedInUrl"}
                                    value={organizationModel?.linkedInUrl}
                                    styles={styles.socialInput}
                                    handleChange={handleInputChange}
                                    handleBlur={handleInputBlur}                           
                                />
                            </div>
                        </div>
                        <div style={styles.inputWrapper}>
                            <label style={styles.socialInputLabel}>{"Facebook"}</label>
                            <div style={styles.inputWithIcon}>
                                <div style={styles.socialIconWrapper}>
                                    <Facebook style={styles.socialIcon}/>
                                </div>
                                <TextInput
                                    inputType={"text"}
                                    name={"facebookUrl"}
                                    value={organizationModel?.facebookUrl}
                                    styles={styles.socialInput}
                                    handleChange={handleInputChange}
                                    handleBlur={handleInputBlur}                            
                                />
                            </div>
                        </div>
                        <div style={styles.inputWrapper}>
                            <label style={styles.socialInputLabel}>{"X"}</label>
                            <div style={styles.inputWithIcon}>
                                <div style={styles.socialIconWrapper}>
                                    <Twitter style={{...styles.socialIcon, ...styles.socialIcon.twitter}}/>
                                </div>
                                <TextInput
                                    inputType={"text"}
                                    name={"twitterUrl"}
                                    value={organizationModel?.twitterUrl}
                                    styles={styles.socialInput}
                                    handleChange={handleInputChange}
                                    handleBlur={handleInputBlur}                               
                                />
                            </div>
                        </div>
                        <div style={styles.inputWrapper}>
                            <label style={styles.socialInputLabel}>{"YouTube"}</label>
                            <div style={styles.inputWithIcon}>
                                <div style={styles.socialIconWrapper}>
                                    <Youtube style={styles.socialIcon}/>
                                </div>
                                <TextInput
                                    inputType={"text"}
                                    name={"youtubeUrl"}
                                    value={organizationModel?.youtubeUrl}
                                    styles={styles.socialInput}
                                    handleChange={handleInputChange}
                                    handleBlur={handleInputBlur}                              
                                />
                            </div>
                        </div>
                        <hr style={styles.line}/>
                        <div style={styles.inputWrapper}>
                            <label style={styles.label}>{"Video"}</label>
                            <TextInput
                                inputType={"text"}
                                name={"video"}
                                value={organizationModel?.video ? organizationModel.video.trim() : ""}
                                styles={styles.textInput}
                                placeHolder={"Paste link Here"}
                                handleChange={handleInputChange}
                                handleBlur={handleInputBlur}                              
                            /> 
                        </div>
                        <Button style={styles.saveButton}>{"SAVE CHANGES"}</Button>               
                    </form>
                    <PopUpWindow open={uploadedLogo != undefined} styles={styles.uploadImagePopup} toggle={removeSelectedLogo}>
                        <img style={styles.uploadedImage} src={logoURL} alt="Organization Logo" />
                        <Button style={styles.uploadImageButton} disabled={logoUploading} onClick={handleLogoUpload}>{"Add Organization Logo"}</Button>
                    </PopUpWindow>
                    <Alert/>   
                </div> 
            </div>}
        </>
    )
}